// Fetching code ahead of time, while the browser has nothing else to do. Opening a page for the first time then shows
// it at once instead of an empty screen while its code downloads. Not on a connection where the user asked to save
// data or that is very slow: there pages keep loading when they are opened.

/** False when the browser reports Save-Data or a 2G connection (navigator.connection, not in every browser). */
export function prefetchAllowed(nav = globalThis.navigator) {
  const connection = nav?.connection
  if (!connection) return true
  return !connection.saveData && !/2g$/.test(connection.effectiveType ?? '')
}

/** Resolves once the document has loaded (its `load` event), so what the current page needs comes first. */
export function whenLoaded(doc = globalThis.document) {
  if (!doc || doc.readyState === 'complete') return Promise.resolve()
  return new Promise((resolve) => globalThis.addEventListener('load', () => resolve(), { once: true }))
}

/** Resolves when the browser is idle (or after a short pause where it cannot tell). */
export function nextIdle() {
  return new Promise((resolve) => {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(() => resolve(), { timeout: 2000 })
    else setTimeout(resolve, 50)
  })
}

/**
 * Runs `tasks` (functions returning a promise) in idle time, `concurrency` at a time, in order. A failed task does
 * not stop the others. Resolves when all have settled.
 */
export async function runWhenIdle(tasks, { concurrency = 3, idle = nextIdle } = {}) {
  const queue = [...tasks]
  async function worker() {
    while (queue.length) {
      const task = queue.shift()
      await idle()
      try {
        await task()
      } catch (e) {
        // what could not be fetched now is fetched again when it is needed
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker))
}
